import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import { Avatar, Box, Tooltip, Typography } from '@mui/material';
import { alpha, darken, useTheme } from '@mui/material/styles';
import moment from 'moment';
import { EventProps } from 'react-big-calendar';
import { CALENDAR_MESSAGES } from 'dashboard409a/common/constants/calendar';
import { getCalendarColorPalette, getStatusColor } from 'dashboard409a/hooks/useColorPalette';
import { getVacationColors } from 'dashboard409a/hooks/useVacationColors';
import { reverseModelStatusMap } from 'dashboard409a/pages/Operations/components/ModelStatus/ModelStatus.constants';
import { getEngagementTypeColor } from 'dashboard409a/utilities/colorPalette';
import { getInitials } from 'utilities/getInitials';
import {
  chipsRowBoxSx,
  companyNameTypographySx,
  companyRowBoxSx,
  engagementChipOuterSx,
  engagementChipTextSx,
  eventRootSx,
  eventShellSx,
  labelChipOuterSx,
  labelChipTextSx,
  notesCountChipSx,
  notesCountIconSx,
  notesCountTextSx,
  notesTooltipAuthorTypographySx,
  notesTooltipBodyTypographySx,
  notesTooltipContentSx,
  peopleGroupBoxSx,
  peopleRowBoxSx,
  personAvatarDimensionsSx,
  statusBannerSx,
  statusLabelTypographySx,
  vacationEventRootSx,
  vacationIconSx,
  vacationTitleSx,
  waitingBadgeBoxSx,
} from './EventComponent.styles';
import type { CalendarPersonProfile } from './EventComponent.types';
import { buildLatestNotePreviewHtml } from './EventComponent.utilities';
import { useCalendarHover } from '../../CalendarHoverContext';
import { CalendarEvent, VacationCalendarEvent, VacationToggleCalendarEvent } from '../../ModelsCalendar.types';

const isVacationCalendarEvent = (event: EventProps['event']): event is VacationCalendarEvent =>
  (event as VacationCalendarEvent).isGoogleVacationEvent === true;

const isVacationToggleEvent = (event: EventProps['event']): event is VacationToggleCalendarEvent =>
  (event as VacationToggleCalendarEvent).isVacationToggleEvent === true;

const EventComponent = (props: EventProps) => {
  const theme = useTheme();
  const { hoveredVacationKey, setHoveredVacationKey } = useCalendarHover();
  const { event } = props;

  if (isVacationToggleEvent(event)) {
    return (
      <Box sx={eventShellSx}>
        <button className="models-calendar-vacation-toggle" type="button">
          {event.title}
        </button>
      </Box>
    );
  }

  if (isVacationCalendarEvent(event)) {
    const vacationKey = `${event.title}|${(event.start as Date | undefined)?.toISOString() ?? ''}`;
    const isHovered = hoveredVacationKey === vacationKey;

    const { bg: storedBg, text: storedText } = getVacationColors();
    const vacationBg = theme.palette.mode === 'dark' ? alpha(storedBg, 0.28) : storedBg;
    const vacationText = storedText;

    return (
      <Box sx={eventShellSx}>
        <Tooltip title="Google Calendar" placement="top" arrow>
          <Box
            sx={vacationEventRootSx(vacationBg, isHovered)}
            onMouseEnter={() => setHoveredVacationKey(vacationKey)}
            onMouseLeave={() => setHoveredVacationKey(null)}
          >
            <CalendarMonthIcon sx={vacationIconSx(vacationText)} />
            <Typography variant="caption" fontWeight="bold" sx={vacationTitleSx(vacationText)}>
              {event.title}
            </Typography>
          </Box>
        </Tooltip>
      </Box>
    );
  }

  const calendarEvent = event as CalendarEvent;
  const model = calendarEvent.resource;

  const calendarPalette = getCalendarColorPalette();
  const labelsBackgroundColor = calendarPalette.LABELS;
  const labelsTextColor = theme.palette.getContrastText(labelsBackgroundColor);
  const waitingBackgroundColor = calendarPalette.WAITING;
  const waitingTextColor = theme.palette.getContrastText(waitingBackgroundColor);
  const engagementBackgroundColor = model.engagement_type ? getEngagementTypeColor(model.engagement_type) : null;
  const engagementTextColor = engagementBackgroundColor ? theme.palette.getContrastText(engagementBackgroundColor) : null;

  const companyName = model.company_name || CALENDAR_MESSAGES.UNKNOWN_COMPANY;
  const valuationDate = model.valuation_date ? moment(model.valuation_date).format('MMM, DD, YY') : '';
  const statusColor = getStatusColor(model.model_status ?? 0);
  const statusLabel = reverseModelStatusMap[model.model_status as keyof typeof reverseModelStatusMap] || '';

  const hasNotes = typeof model.notes_count === 'number' && model.notes_count > 0;
  const latestNote = model.note;
  const latestNoteAuthor = latestNote?.author_name || latestNote?.author_email || '';
  const latestNoteCreatedAt = latestNote?.created_at ? moment(latestNote.created_at).format('MMM D, YYYY h:mm A') : '';
  const latestNotePreviewHtml = buildLatestNotePreviewHtml((latestNote?.content ?? '').trim());

  const preparers = (model.preparers ?? []) as unknown as CalendarPersonProfile[];
  const reviewers = (model.reviewers ?? []) as unknown as CalendarPersonProfile[];

  const avatarBg = darken(labelsBackgroundColor, 0.2);
  const avatarInitialsColor = theme.palette.getContrastText(avatarBg);

  return (
    <Box sx={{ ...eventShellSx, cursor: 'pointer' }}>
      <Box sx={{ ...eventRootSx, opacity: model.is_waiting ? 0.5 : 1, backgroundColor: calendarPalette.BACKGROUND }}>
        <Box sx={statusBannerSx(statusColor)}>
          <Typography variant="caption" sx={statusLabelTypographySx(statusColor)}>
            {statusLabel}
          </Typography>
        </Box>
        <Box sx={companyRowBoxSx}>
          <Typography variant="caption" fontWeight="bold" sx={companyNameTypographySx(getCalendarColorPalette().BACKGROUND)}>
            {companyName}
          </Typography>
        </Box>
        <Box sx={chipsRowBoxSx}>
          {model.engagement_type && engagementBackgroundColor && engagementTextColor && (
            <Box sx={engagementChipOuterSx(engagementBackgroundColor, engagementTextColor)}>
              <Typography variant="caption" sx={engagementChipTextSx(engagementTextColor)}>
                {model.engagement_type}
              </Typography>
            </Box>
          )}
          {valuationDate && (
            <Box sx={labelChipOuterSx(labelsBackgroundColor)}>
              <Typography variant="caption" sx={labelChipTextSx(labelsTextColor)}>
                {valuationDate}
              </Typography>
            </Box>
          )}
          {hasNotes && (
            <Tooltip
              arrow
              placement="top"
              title={
                <Box sx={notesTooltipContentSx}>
                  <Typography variant="caption" sx={notesTooltipAuthorTypographySx}>
                    {latestNoteAuthor || 'Analyst'} {latestNoteCreatedAt ? `• ${latestNoteCreatedAt}` : ''}
                  </Typography>
                  <Typography
                    variant="body2"
                    component="div"
                    sx={notesTooltipBodyTypographySx}
                    dangerouslySetInnerHTML={{ __html: latestNotePreviewHtml || '—' }}
                  />
                </Box>
              }
            >
              <Box sx={notesCountChipSx(labelsBackgroundColor)}>
                <ChatBubbleOutlineIcon sx={notesCountIconSx(labelsTextColor)} />
                <Typography variant="caption" sx={notesCountTextSx(labelsTextColor)}>
                  {model.notes_count}
                </Typography>
              </Box>
            </Tooltip>
          )}
        </Box>
        <Box sx={peopleRowBoxSx}>
          <Box sx={peopleGroupBoxSx}>
            {preparers.map(preparer => (
              <Avatar
                key={preparer.id}
                src={preparer.picture_url ?? undefined}
                sx={{ ...personAvatarDimensionsSx, color: avatarInitialsColor }}
                style={{ backgroundColor: avatarBg }}
              >
                {getInitials({ name: `${preparer.first_name || ''} ${preparer.last_name || ''}` })}
              </Avatar>
            ))}
          </Box>
          <Box sx={peopleGroupBoxSx}>
            {reviewers.map(reviewer => (
              <Avatar
                key={reviewer.id}
                src={reviewer.picture_url ?? undefined}
                sx={{ ...personAvatarDimensionsSx, color: avatarInitialsColor }}
                style={{ backgroundColor: avatarBg }}
              >
                {getInitials({ name: `${reviewer.first_name || ''} ${reviewer.last_name || ''}` })}
              </Avatar>
            ))}
          </Box>
        </Box>
      </Box>
      {model.is_waiting && (
        <Box sx={waitingBadgeBoxSx(waitingBackgroundColor)}>
          <Typography variant="caption" fontSize="0.7rem" sx={{ color: waitingTextColor }}>
            WAITING
          </Typography>
        </Box>
      )}
    </Box>
  );
};

export default EventComponent;
